import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AlertStore } from '@shared/components/alert/data-access/alert.store';
import { EAlertTypes } from '@shared/components/alert/data-access/entities/alert.enum';

@Injectable({
    providedIn: 'root'
})
export class ErrorHandlerService {
    private alertStore = inject(AlertStore);

    private openAlert = (message: string, type: EAlertTypes, title?: string): void => {
        this.alertStore.openAlert({
            title: title ?? 'Ops! Algo deu errado.',
            type,
            message
        });
    }

    private getErrorMessage = (error: HttpErrorResponse): string | null => {

        if (error?.error?.errors) {
            const errorKeys = Object.keys(error.error.errors);
            return error.error.errors[errorKeys[0]][0];
        }

        if (error?.error?.Validacao?.Erros) {
            return error.error.Validacao.Erros;
        }

        if (error?.error?.ValidationResult?.Errors) {
            return error.error.ValidationResult.Errors.map((element: any) => element.ErrorMessage)[0];
        }

        if (error?.error?.ModelState?.Erros) {
            return error.error.ModelState.Erros;
        }

        if (error?.error?.errors?.DomainValidations) {
            return error.error.errors.DomainValidations;
        }

        if (error?.error?.Erros) {
            return error.error.Erros;
        }

        return null;
    }

    /** Extrai a mensagem de erro do backend a partir de um HttpErrorResponse. */
    public getHttpErrorMessage(error: HttpErrorResponse): string | null {
        return this.getErrorMessage(error);
    }

    /**
     * Alerta de assinatura inativa. Disparado apenas quando um 403 vem de uma
     * requisição crítica de conta/usuário (ver TokenInterceptor.shouldLogoutOn403),
     * e não em qualquer 403 espalhado pelo app.
     */
    public handleInactiveSubscription(): void {
        this.alertStore.openAlert({
            id: 'inactive-subscription',
            title: 'Assinatura Inativa',
            type: EAlertTypes.WARNING,
            message: 'Entre em contato com a sua academia para mais informações.',
        });
    }

    /**
     * 403 inesperado — fora dos endpoints de conta (que deslogam) e do
     * /Subscription (que abre o gate). O app não usa permissões, então isto só
     * ocorre se o backend setar 403 por engano. Alerta genérico deduplicado
     * (id fixo) para o usuário não ficar sem feedback. Ver TokenInterceptor.
     */
    public handleForbidden(): void {
        this.alertStore.openAlert({
            id: 'forbidden',
            title: 'Acesso negado',
            type: EAlertTypes.WARNING,
            message: 'Você não tem permissão para acessar este conteúdo.',
        });
    }

    public handleError(error: HttpErrorResponse): void {
        if (error.status === 403) return;

        const errorMessages: string | null = this.getErrorMessage(error);
        let type: EAlertTypes = EAlertTypes.WARNING;
        if (error.status >= 500 && error.status < 600) type = EAlertTypes.DANGER;

        if (errorMessages) {
            this.openAlert(errorMessages, type);
            return;
        }

        if (error.status === 400) {
            this.openAlert('Houve um problema ao processar sua solicitação.', type);
            return;
        }

        if (error.status === 404) {
            this.openAlert('O recurso não foi encontrado', type);
            return;
        }

        if (error.status === 401) {
            this.openAlert('Você não tem permissão para acessar este recurso', type);
            return;
        }

        if (error.status === 500 || error.status === 0) {
            this.openAlert('Algo não saiu como esperado. Nossa equipe já foi notificada e está investigando.', type);
            return;
        }
    }

}